import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { SUPPORTS } from "./Projects";
import { GraduationCap, Plus, Trash2, Pencil, Lock, Sparkles, Play, X, LayoutTemplate, ArrowRight } from "lucide-react";

const DEFAULT_LABELS = ["Demande reçue", "Qualification", "Étude & cotes", "Devis", "Acompte reçu", "Commande matières", "Production", "Contrôle qualité", "Livraison", "Solde & facture", "Avis client"];
const ytEmbed = (u) => { if (!u) return null; const m = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/); return m ? `https://www.youtube.com/embed/${m[1]}` : null; };

export default function Templates() {
  const navigate = useNavigate();
  const [data, setData] = useState({ premium: false, own: [], academy: [] });
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/project-templates").then((r) => setData(r.data || { premium: false, own: [], academy: [] })).catch(() => {});
  useEffect(() => { load(); }, []);

  const applyTemplate = async (t) => {
    try { const { data: proj } = await api.post(`/projects/from-template/${t.id}`); toast.success("Projet créé depuis le modèle"); navigate(`/projets/${proj.id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const remove = async (t) => { if (!window.confirm(`Supprimer le modèle « ${t.title} » ?`)) return; await api.delete(`/project-templates/${t.id}`); toast.success("Modèle supprimé"); load(); };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-navy flex items-center justify-center"><LayoutTemplate className="w-5 h-5 text-cognac-400" /></div>
          <div>
            <h1 className="text-3xl font-display font-bold text-navy">Projets types</h1>
            <p className="text-slate-500">Des modèles prêts à l'emploi, avec toutes les étapes déjà développées</p>
          </div>
        </div>
        <button onClick={() => setEditing({})} data-testid="new-template-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-cognac-500 text-white font-semibold hover:bg-cognac-600"><Plus className="w-4 h-4" /> Nouveau modèle</button>
      </div>

      {/* Sellerie Académie */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <GraduationCap className="w-5 h-5 text-cognac-600" />
          <h2 className="font-display font-semibold text-navy text-lg">Sellerie Académie</h2>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-cognac-100 text-cognac-700 font-semibold inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Premium</span>
        </div>
        {!data.premium && (
          <div className="mb-3 rounded-xl border border-cognac-200 bg-cognac-50 px-4 py-3 flex items-center gap-3" data-testid="premium-banner">
            <Lock className="w-5 h-5 text-cognac-600 shrink-0" />
            <div className="flex-1 min-w-0 text-sm text-cognac-700">Les modèles Sellerie Académie et leurs tutos vidéo sont réservés à la <b>licence Premium</b>. Activez-la dans Paramètres.</div>
            <button onClick={() => navigate("/parametres")} data-testid="goto-premium-btn" className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-cognac-500 text-white hover:bg-cognac-600 shrink-0">Activer</button>
          </div>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.academy.map((t) => <TemplateCard key={t.id} t={t} academy premium={data.premium} onUse={() => applyTemplate(t)} />)}
        </div>
      </div>

      {/* Mes modèles */}
      <div>
        <h2 className="font-display font-semibold text-navy text-lg mb-3">Mes modèles</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.own.map((t) => <TemplateCard key={t.id} t={t} onUse={() => applyTemplate(t)} onEdit={() => setEditing(t)} onDelete={() => remove(t)} />)}
          {data.own.length === 0 && <div className="col-span-full text-sm text-slate-400 py-8 text-center border border-dashed border-slate-200 rounded-2xl">Aucun modèle personnel. Créez-en un ou dupliquez le déroulé standard.</div>}
        </div>
      </div>

      {editing && <TemplateModal template={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function TemplateCard({ t, academy, premium, onUse, onEdit, onDelete }) {
  const sup = SUPPORTS[t.support_type] || SUPPORTS.autre;
  const SupIcon = sup.icon;
  const locked = t.locked;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col" data-testid={`template-card-${t.id}`}>
      <div className="flex items-start justify-between gap-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${sup.color}`}><SupIcon className="w-3 h-3" /> {sup.label}</span>
        {academy && <span className="text-[10px] px-2 py-0.5 rounded-full bg-cognac-100 text-cognac-700 font-semibold inline-flex items-center gap-1"><GraduationCap className="w-3 h-3" /> Académie</span>}
      </div>
      <h3 className="mt-2 font-display font-bold text-navy text-base leading-snug">{t.title}</h3>
      {t.description && <p className="mt-1 text-xs text-slate-500 line-clamp-2">{t.description}</p>}
      <div className="mt-3 space-y-1 flex-1">
        {(t.stages || []).slice(0, 5).map((s, i) => (
          <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
            <span className="w-4 h-4 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-[9px] font-bold shrink-0">{i + 1}</span>
            <span className="truncate">{s.label}</span>
            {!locked && s.video_url && <Play className="w-3 h-3 text-cognac-500 shrink-0" />}
          </div>
        ))}
        {(t.stages || []).length > 5 && <div className="text-[11px] text-slate-400 pl-6">+{(t.stages || []).length - 5} autres étapes</div>}
      </div>
      <div className="mt-4 flex items-center gap-2">
        {locked ? (
          <button disabled className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 text-slate-400 text-sm font-semibold cursor-not-allowed" data-testid={`template-locked-${t.id}`}><Lock className="w-4 h-4" /> Premium requis</button>
        ) : (
          <button onClick={onUse} data-testid={`template-use-${t.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-navy text-white text-sm font-semibold hover:bg-navy-deep">Utiliser <ArrowRight className="w-4 h-4" /></button>
        )}
        {onEdit && <button onClick={onEdit} data-testid={`template-edit-${t.id}`} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"><Pencil className="w-4 h-4" /></button>}
        {onDelete && <button onClick={onDelete} data-testid={`template-delete-${t.id}`} className="p-2 rounded-lg border border-slate-200 text-rose-500 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>}
      </div>
    </div>
  );
}

function TemplateModal({ template, onClose, onSaved }) {
  const isNew = !template.id;
  const [f, setF] = useState({ title: "", support_type: "automobile", description: "", stages: [{ label: "", memo: "", video_url: "" }], ...template });
  const [saving, setSaving] = useState(false);
  const setStage = (i, k, v) => setF((p) => ({ ...p, stages: p.stages.map((s, j) => j === i ? { ...s, [k]: v } : s) }));
  const addStage = () => setF((p) => ({ ...p, stages: [...p.stages, { label: "", memo: "", video_url: "" }] }));
  const delStage = (i) => setF((p) => ({ ...p, stages: p.stages.filter((_, j) => j !== i) }));
  const loadStd = () => setF((p) => ({ ...p, stages: DEFAULT_LABELS.map((l) => ({ label: l, memo: "", video_url: "" })) }));
  const save = async () => {
    if (!f.title.trim()) { toast.error("Titre requis"); return; }
    const stages = f.stages.filter((s) => s.label.trim());
    if (!stages.length) { toast.error("Ajoutez au moins une étape"); return; }
    setSaving(true);
    try {
      const payload = { title: f.title, support_type: f.support_type, description: f.description, stages };
      if (isNew) await api.post("/project-templates", payload); else await api.put(`/project-templates/${template.id}`, payload);
      toast.success(isNew ? "Modèle créé" : "Modèle mis à jour"); onSaved();
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="template-modal">
        <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-display font-bold text-navy">{isNew ? "Nouveau modèle" : "Modifier le modèle"}</h3><button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5 text-slate-400" /></button></div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2"><label className="text-xs text-slate-500">Titre</label><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="tpl-title" className={inp} placeholder="Ex. Réfection banquette 2 places" /></div>
          <div><label className="text-xs text-slate-500">Support</label><select value={f.support_type} onChange={(e) => setF({ ...f, support_type: e.target.value })} data-testid="tpl-support" className={inp}>{Object.entries(SUPPORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></div>
          <div className="col-span-3"><label className="text-xs text-slate-500">Description</label><input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="tpl-desc" className={inp} /></div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm font-semibold text-navy">Étapes ({f.stages.length})</span>
          <button onClick={loadStd} className="text-xs text-sky-600 font-semibold hover:underline">Charger le déroulé standard</button>
        </div>
        <div className="mt-2 space-y-2">
          {f.stages.map((s, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-3" data-testid={`tpl-stage-${i}`}>
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-[10px] font-bold shrink-0">{i + 1}</span>
                <input value={s.label} onChange={(e) => setStage(i, "label", e.target.value)} placeholder="Nom de l'étape" data-testid={`tpl-stage-label-${i}`} className="flex-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cognac-400" />
                <button onClick={() => delStage(i)} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
              </div>
              <textarea value={s.memo} onChange={(e) => setStage(i, "memo", e.target.value)} rows={2} placeholder="Mémo / méthode (comment réaliser cette étape)…" className="mt-2 w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs resize-none focus:outline-none focus:ring-2 focus:ring-cognac-400" />
              <input value={s.video_url} onChange={(e) => setStage(i, "video_url", e.target.value)} placeholder="Lien tuto vidéo (YouTube/Vimeo) — optionnel" className="mt-1.5 w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-cognac-400" />
            </div>
          ))}
        </div>
        <button onClick={addStage} data-testid="tpl-add-stage" className="mt-2 inline-flex items-center gap-1.5 text-sm text-cognac-600 font-semibold hover:underline"><Plus className="w-4 h-4" /> Ajouter une étape</button>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold">Annuler</button>
          <button onClick={save} disabled={saving} data-testid="tpl-save" className="px-4 py-2.5 rounded-xl bg-cognac-500 text-white font-semibold hover:bg-cognac-600 disabled:opacity-60">Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

const inp = "mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cognac-400";
export { ytEmbed };
